import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './DeleteWidgetModal.css';

/**
 * Modal for confirming widget deletion
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback when modal closes
 * @param {function} props.onConfirm - Callback when deletion is confirmed
 * @param {string} props.widgetName - Name of the widget to delete
 * @param {boolean} props.isDeleting - Whether deletion is in progress
 * @returns {JSX.Element} Modal component
 */
const DeleteWidgetModal = ({ isOpen, onClose, onConfirm, widgetName, isDeleting }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking the overlay itself, not the modal content
    if (e.target === e.currentTarget && !isDeleting) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="delete-widget-modal">
        <div className="modal-header">
          <h2>{t('dashboards.deleteWidget')}</h2>
          {!isDeleting && (
            <button 
              className="modal-close-btn" 
              onClick={handleCancel}
              aria-label={t('common.close')}
            >
              ×
            </button>
          )}
        </div>

        <div className="modal-content">
          <p className="delete-confirmation-text">
            {t('dashboards.deleteWidgetConfirm', { name: widgetName })}
          </p>
          <p className="delete-warning-text">
            {t('dashboards.deleteWidgetWarning')}
          </p>
        </div>

        <div className="modal-actions">
          <button 
            className="cancel-btn" 
            onClick={handleCancel}
            disabled={isDeleting}
          >
            {t('common.cancel')}
          </button>
          <button 
            className="delete-btn" 
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? t('dashboards.deleting') : t('dashboards.delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteWidgetModal;

